import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import Rx from "rxjs"
import type { CustomerLoyaltyProgramStatsItem } from "../components/data/types"
import type { OperationsBatch, SendOperationsResponse } from "../components/operations/types"
import type { CustomersFeedbackApiParameters } from "../components/screens/reports/customers/customersFeedback/selectors"
import type { OrderReportApiParameters } from "../components/screens/reports/orders/types"
import type { QueryApiParameters } from "../utils/queryApi/queryApiRequest"
import { AccessLevelDTO, getAccessAndRolesApi } from "./accessAndRoles"
import type { GetAccountImageServerResponse } from "./accountImage"
import { getAccountImageApi } from "./accountImage"
import type { GetCustomerServerResponse } from "./customerDetail"
import { getCustomerDetailApi } from "./customerDetail"
import { getCustomerLoyaltyProgramStatsApi } from "./customerLoyaltyStats"
import type { CustomerOrderItem } from "./customerOrders"
import { getCustomerOrdersApi } from "./customerOrders"
import { getDepartmentApi } from "./department"
import { getGenericRecordById } from "./genericRecord"
import type { GetInventoryServerResponse } from "./inventory"
import { getInventoryApi } from "./inventory"
import type { InventoryLogResponseItem } from "./inventoryLogOfChanges"
import { getInventoryLogOfChanges } from "./inventoryLogOfChanges"
import type { LoginResponsePayload } from "./login"
import { login } from "./login"
import { logout } from "./logout"
import type { GetLoyaltyProgramResponse } from "./loyaltyProgram"
import { getLoyaltyProgramApi } from "./loyaltyProgram"
import type { GetCustomersFeedbackReportServerResponse } from "./reports/customersFeedbackReport"
import { getCustomersFeedbackReportApi } from "./reports/customersFeedbackReport"
import { downloadReport } from "./reports/downloadReport"
import type { GetOrdersReportServerResponse } from "./reports/ordersReport"
import { getOrdersCsvReportApi } from "./reports/ordersReport"
import { getRoleApi } from "./role"
import { sendOperations } from "./sendOperations"
import type { HttpHeaders, RecordType } from "./types"
import { getCsvReportApi, saveAccountImageApi } from "./uploadImage"
import type { GetUserAccountServerResponse } from "./userAccount"
import { getUserAccountApi } from "./userAccount"
import { DepartmentDTO, RoleDTO } from "../components/screens/employees/positions/types"

export type ApiServices = {
    login: (headers: HttpHeaders, email: string, password: string) => Rx.Observable<LoginResponsePayload>
    logout: (headers: HttpHeaders) => Rx.Observable<boolean>
    downloadReport: (
        headers: HttpHeaders,
        reportAgendaApiRoute: string,
        queryApiParameters: QueryApiParameters
    ) => Rx.Observable<void>
    getCustomerDetailApi: (headers: HttpHeaders, id: string) => Rx.Observable<GetCustomerServerResponse>
    getCustomerOrdersApi: (headers: HttpHeaders, id: string) => Rx.Observable<CustomerOrderItem[]>
    getCustomerLoyaltyProgramStatsApi: (
        headers: HttpHeaders,
        id: string
    ) => Rx.Observable<CustomerLoyaltyProgramStatsItem>
    getCustomersFeedbackReportApi: (
        headers: HttpHeaders,
        config: CustomersFeedbackApiParameters
    ) => Rx.Observable<GetCustomersFeedbackReportServerResponse>
    getOrdersCsvReportApi: (
        headers: HttpHeaders,
        config: OrderReportApiParameters
    ) => Rx.Observable<GetOrdersReportServerResponse>
    getCsvReportApi: (headers: HttpHeaders, id: string, url: Maybe<string>) => Rx.Observable<any>
    sendOperations: (headers: HttpHeaders, batch: OperationsBatch) => Rx.Observable<SendOperationsResponse>
    getUserAccountApi: (
        headers: HttpHeaders,
        parameters?: Record<string, any>
    ) => Rx.Observable<GetUserAccountServerResponse>
    getRoleApi: (headers: HttpHeaders) => Rx.Observable<RoleDTO[]>
    getAccessAndRolesApi: (headers: HttpHeaders) => Rx.Observable<AccessLevelDTO[]>
    getDepartmentApi: (headers: HttpHeaders) => Rx.Observable<DepartmentDTO[]>
    // eslint-disable-next-line
    saveAccountImageApi: (
        headers: HttpHeaders,
        accountId: string,
        file: any,
        type: string,
        terminalId?: Maybe<string>,
        id?: Maybe<string>,
        sortIndex?: Maybe<number>,
        label?: Maybe<string>
    ) => Rx.Observable<void>
    getLoyaltyProgramApi: (headers: HttpHeaders) => Rx.Observable<GetLoyaltyProgramResponse>
    getInventoryApi: (headers: HttpHeaders) => Rx.Observable<GetInventoryServerResponse>
    getAccountImageApi: (headers: HttpHeaders) => Rx.Observable<GetAccountImageServerResponse>
    getGenericRecordById: (
        headers: HttpHeaders,
        recordType: RecordType,
        recordId: string
    ) => Rx.Observable<Maybe<Record<string, any>>>
    getInventoryLogOfChanges: (
        headers: HttpHeaders,
        invItemId: string,
        locationId: Maybe<string>
    ) => Rx.Observable<InventoryLogResponseItem[]>
}

export const createApiServices = (): ApiServices => ({
    login: (headers: HttpHeaders, email: string, password: string) => login(headers, email, password),
    logout: (headers: HttpHeaders) => logout(headers),
    downloadReport: (headers: HttpHeaders, reportAgendaApiRoute: string, parameters: Record<string, any>) =>
        downloadReport(headers, reportAgendaApiRoute, parameters),
    getCustomersFeedbackReportApi: (headers: HttpHeaders, config: CustomersFeedbackApiParameters) =>
        getCustomersFeedbackReportApi(headers, config),
    getOrdersCsvReportApi: (headers: HttpHeaders, config: OrderReportApiParameters) =>
        getOrdersCsvReportApi(headers, config),
    getCsvReportApi: (headers: HttpHeaders, id: string) => getCsvReportApi(headers, id),
    getCustomerDetailApi: (headers: HttpHeaders, id: string) => getCustomerDetailApi(headers, id),
    getCustomerOrdersApi: (headers: HttpHeaders, id: string) => getCustomerOrdersApi(headers, id),
    getCustomerLoyaltyProgramStatsApi: (headers: HttpHeaders, id: string) =>
        getCustomerLoyaltyProgramStatsApi(headers, id),
    sendOperations: (headers: HttpHeaders, batch: OperationsBatch) => sendOperations(headers, batch),
    getUserAccountApi: (headers: HttpHeaders, parameters: Record<string, any> = {}) =>
        getUserAccountApi(headers, parameters),
    getRoleApi: (headers: HttpHeaders) => getRoleApi(headers),
    getAccessAndRolesApi: (headers: HttpHeaders) => getAccessAndRolesApi(headers),
    getDepartmentApi: (headers: HttpHeaders) => getDepartmentApi(headers),

    saveAccountImageApi: (
        headers: HttpHeaders,
        accountId: string,
        file: any,
        type: string,
        terminalId?: Maybe<string>,
        // @ts-expect-error
        id: Maybe<string>,
        sortIndex?: Maybe<number>,
        label?: Maybe<string>
    ) => saveAccountImageApi(headers, accountId, file, type, terminalId, id, sortIndex, label),
    getAccountImageApi: (headers: HttpHeaders) => getAccountImageApi(headers),
    getLoyaltyProgramApi: (headers: HttpHeaders) => getLoyaltyProgramApi(headers),
    getInventoryApi: (headers: HttpHeaders) => getInventoryApi(headers),
    getGenericRecordById: (headers: HttpHeaders, recordType: RecordType, recordId: string) =>
        getGenericRecordById(headers, recordType, recordId),
    getInventoryLogOfChanges: (headers: HttpHeaders, invItemId: string, locationId: Maybe<string>) =>
        getInventoryLogOfChanges(headers, invItemId, locationId),
})
